import axios from 'axios'
import { emitSessionExpired } from './authEvents'

const API_URL = import.meta.env.VITE_API_URL || ''

// VH-119: auth endpoints that must NEVER trigger the refresh-and-retry flow
// (a 401 from login = bad credentials; a 401 from refresh = dead session).
const AUTH_LOGIN_PATH = '/api/v1/auth/login'
const AUTH_REFRESH_PATH = '/api/v1/auth/refresh'
// VH-284: a 401 from logout means the session had ended already.
const AUTH_LOGOUT_PATH = '/api/v1/auth/logout'
// VH-285: the first set-up runs on a login ticket, not a session; its 401
// means the ticket ran out. (/auth/login covers /auth/login/code.)
const AUTH_SETUP_PATHS = ['/api/v1/auth/second-factor/setup', '/api/v1/auth/second-factor/activate']

// VH-284 / VH-293: the login's lifecycle notes live in authKeys; re-exported
// here for the callers that always imported them from the API client.
export {
  LOGIN_USER_KEY,
  LOGOUT_REASON_EXPIRED,
  LOGOUT_REASON_IDLE,
  LOGOUT_REASON_KEY,
} from './authKeys'

export const api = axios.create({
  baseURL: API_URL,
  // VH-304: the refresh cookie must reach the API on SIT too, where it runs
  // on its own port (on the boxes the API is the page's own origin).
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token and handle FormData
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // When sending FormData, let axios set the correct Content-Type with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    return config
  },
  (error) => {
    console.error('[API Request Error]', error)
    return Promise.reject(error)
  }
)

// ---------------------------------------------------------------------------
// VH-119: silent access-token refresh + single-flight retry
// ---------------------------------------------------------------------------

function isAuthFlowRequest(url = '') {
  return (
    url.includes(AUTH_LOGIN_PATH) ||
    url.includes(AUTH_REFRESH_PATH) ||
    url.includes(AUTH_LOGOUT_PATH) ||
    AUTH_SETUP_PATHS.some((path) => url.endsWith(path))
  )
}

function clearTokens() {
  localStorage.removeItem('token')
  // VH-304: only a page from before the switch still holds one.
  localStorage.removeItem('refresh_token')
}

// Shared in-flight refresh promise so concurrent 401s trigger exactly ONE
// POST /auth/refresh; all queued requests await the same promise.
let refreshPromise = null

/**
 * Exchange the refresh cookie for a new access token (single-flight).
 * Uses a BARE axios call — NOT the `api` instance — to avoid recursing back
 * into this very interceptor. Persists the new access token. Resolves with
 * the new access token; rejects if the refresh request fails.
 *
 * VH-304 (SEC-006): the refresh token is an HttpOnly cookie the browser sends
 * by itself; no script can read it. VH-309: a token a page stored before that
 * switch is never sent, only removed from localStorage.
 *
 * Exported so the idle-timeout "Extend session" button can reuse the exact
 * same refresh path.
 * @returns {Promise<string>} the new access token
 */
export function refreshAccessToken() {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    const headers = { 'Content-Type': 'application/json' }

    const { data } = await axios.post(
      `${API_URL}${AUTH_REFRESH_PATH}`,
      {},
      { headers, withCredentials: true }
    )

    // Backend returns a Token object: { access_token, ... }.
    // Be defensive about a nested { token: {...} } shape too.
    const newAccess = data?.access_token ?? data?.token?.access_token
    if (!newAccess) {
      throw new Error('Refresh response missing access_token')
    }

    localStorage.setItem('token', newAccess)
    localStorage.removeItem('refresh_token')
    return newAccess
  })().finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

/**
 * Response-error handler (exported for testing). On a 401 from a normal
 * request it attempts a single silent refresh and retries the original
 * request once. If refresh is impossible/fails it clears tokens and emits a
 * single session-expired event (the AuthProvider handles the soft logout).
 */
export async function onResponseError(error) {
  const originalRequest = error.config
  console.error('[API Response Error]', {
    url: originalRequest?.url,
    method: originalRequest?.method,
    status: error.response?.status,
    statusText: error.response?.statusText,
    data: error.response?.data,
  })

  const status = error.response?.status
  const canRetry =
    status === 401 &&
    originalRequest &&
    !originalRequest._retry &&
    !isAuthFlowRequest(originalRequest.url || '')

  if (!canRetry) {
    return Promise.reject(error)
  }

  originalRequest._retry = true
  try {
    const newToken = await refreshAccessToken()
    originalRequest.headers = originalRequest.headers || {}
    originalRequest.headers.Authorization = `Bearer ${newToken}`
    return api(originalRequest)
  } catch (refreshError) {
    // VH-293: no answer at all (network down) is not a refused login: keep the
    // tokens, so the next request or reload can still use them.
    if (refreshError?.isAxiosError && !refreshError.response) {
      return Promise.reject(refreshError)
    }
    // Dedupe: clearTokens() empties storage, so a burst of concurrent 401s
    // that all land here only emits the session-expired event once.
    const hadSession =
      localStorage.getItem('token') || localStorage.getItem('refresh_token')
    clearTokens()
    if (hadSession) {
      emitSessionExpired('refresh_failed')
    }
    return Promise.reject(refreshError)
  }
}

// Response interceptor to handle auth errors
api.interceptors.response.use((response) => response, onResponseError)

// VH-317: Sign in with HubPlatform (platform standard §1.5). The button is a
// plain link: the browser goes to HubPlatform and comes back to /login.
export const HUBPLATFORM_START_URL = `${API_URL}/api/v1/auth/hubplatform/start`

export const hubplatformAPI = {
  status: () => api.get('/api/v1/auth/hubplatform/status'),
  finish: (handoff) => api.post('/api/v1/auth/hubplatform/finish', { handoff }),
  confirmCheck: (handoff) => api.post('/api/v1/auth/hubplatform/confirm-check', { handoff }),
  link: () => api.get('/api/v1/auth/hubplatform/link'),
  // Needs a fresh confirmation: call it through withConfirmation.
  linkStart: () => api.post('/api/v1/auth/hubplatform/link/start'),
  unlink: () => api.delete('/api/v1/auth/hubplatform/link'),
}

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/api/v1/auth/login', credentials),
  // VH-285: the code step, the first set-up and the Security page.
  // VH-317: `hubplatformLink` — "Confirm it's you once": link once this step passes.
  loginCode: (ticket, { code, recoveryCode }, hubplatformLink = null) =>
    api.post('/api/v1/auth/login/code', {
      ticket, code, recovery_code: recoveryCode, hubplatform_link: hubplatformLink,
    }),
  // VH-287: a password chosen with the set-up code from the mail.
  enrol: (email, setupCode, newPassword) =>
    api.post('/api/v1/auth/enrol', { email, setup_code: setupCode, new_password: newPassword }),
  beginSetup: (ticket, setupCode) =>
    api.post('/api/v1/auth/second-factor/setup', { ticket, setup_code: setupCode }),
  activateSetup: (ticket, code) =>
    api.post('/api/v1/auth/second-factor/activate', { ticket, code }),
  confirm: ({ code, recoveryCode }) =>
    api.post('/api/v1/auth/confirm', { code, recovery_code: recoveryCode }),
  // VH-296: the code step with a passkey. → { options } for the phone (409
  // no_passkey: use the code), then the phone's answer → as `loginCode`.
  passkeyOptions: (ticket) => api.post('/api/v1/auth/login/passkey/options', { ticket }),
  loginPasskey: (ticket, credential, hubplatformLink = null) =>
    api.post('/api/v1/auth/login/passkey', { ticket, credential, hubplatform_link: hubplatformLink }),
  // VH-296: the same for the confirmation before a sensitive action.
  confirmPasskeyOptions: () => api.post('/api/v1/auth/confirm/passkey/options'),
  confirmPasskey: (credential) => api.post('/api/v1/auth/confirm/passkey', { credential }),
  getSecurity: () => api.get('/api/v1/auth/security'),
  newRecoveryCodes: () => api.post('/api/v1/auth/second-factor/recovery-codes'),
  beginReplace: () => api.post('/api/v1/auth/second-factor/replace'),
  activateReplace: (code) => api.post('/api/v1/auth/second-factor/replace/activate', { code }),
  getProfile: () => api.get('/api/v1/auth/me'),
  updateProfile: (data) => api.put('/api/v1/auth/me', data),
  // VH-284: real sessions
  logout: () => api.post(AUTH_LOGOUT_PATH),
  getSessions: () => api.get('/api/v1/auth/sessions'),
  endSession: (id) => api.delete(`/api/v1/auth/sessions/${id}`),
  logoutOthers: () => api.post('/api/v1/auth/logout-others'),
}

// VH-296: the user's own passkeys (Security).
export const passkeysAPI = {
  // → { items: [{ id, name, created_at, last_used_at }], max, available }
  list: () => api.get('/api/v1/auth/passkeys'),
  // The two below and `remove` need a confirmed code: call them through withConfirmation.
  addOptions: () => api.post('/api/v1/auth/passkeys/options'),
  add: (name, credential) => api.post('/api/v1/auth/passkeys', { name, credential }),
  rename: (id, name) => api.patch(`/api/v1/auth/passkeys/${id}`, { name }),
  // → { sessions_ended }; every other session of the user ends.
  remove: (id) => api.delete(`/api/v1/auth/passkeys/${id}`),
}

// Blood Tests API
export const bloodTestsAPI = {
  getAll: (params) => api.get('/api/v1/blood-tests/', { params }),
  getById: (id) => api.get(`/api/v1/blood-tests/${id}`),
  upload: (formData) => api.post('/api/v1/blood-tests/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  analyze: (id) => api.post(`/api/v1/blood-tests/${id}/analyze`),
  delete: (id) => api.delete(`/api/v1/blood-tests/${id}`),
  compare: (id) => api.get(`/api/v1/blood-tests/${id}/compare`),
  compareMultiple: (ids) => api.get(`/api/v1/blood-tests/compare?ids=${ids}`),
  getTrends: (id) => api.get(`/api/v1/blood-tests/${id}/trends`),
  // VH-221: why markers cannot be judged, and the deterministic score
  getContext: (id, language = 'en') => api.get(`/api/v1/blood-tests/${id}/context`, { params: { language } }),
  getScore: (id) => api.get(`/api/v1/blood-tests/${id}/score`),
  // VH-233: offer a partial test to the current cycle; "Keep separate" is remembered
  getCycleSuggestion: (id) => api.get(`/api/v1/blood-tests/${id}/cycle-suggestion`),
  dismissCycleSuggestion: (id) => api.post(`/api/v1/blood-tests/${id}/cycle-suggestion/dismiss`),
  getMarkerInfo: (markerName, value, status, language = 'en') => api.get(
    `/api/v1/blood-tests/markers/${encodeURIComponent(markerName)}/info`,
    { params: { value, status, language } }
  ),
  // Translation endpoints
  translate: (id, targetLanguage) => api.post(`/api/v1/blood-tests/${id}/translate`, {
    target_language: targetLanguage
  }),
  // Report endpoints
  generateNarrativeReport: (bloodTestIds, options = {}) => api.post('/api/v1/blood-tests/reports/narrative', {
    blood_test_ids: bloodTestIds,
    target_language: options.targetLanguage ?? 'en',
    include_charts: options.includeCharts ?? true,
    include_risk_assessment: options.includeRiskAssessment ?? true
  }),
  getNarrativeReport: (reportId) => api.get(`/api/v1/blood-tests/reports/narrative/${reportId}`),
  listNarrativeReports: (limit = 10) => api.get('/api/v1/blood-tests/reports/narrative', { params: { limit } }),
}

// Supplements API
export const supplementsAPI = {
  create: (data) => api.post('/api/v1/supplements', data),
  analyzeSupplementPhoto: (formData, signal) => api.post('/api/v1/supplements/analyze-supplement-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    ...(signal && { signal }),
  }),
  addToRegimen: (data) => api.post('/api/v1/supplements/user-supplements/', data),
  getUserSupplements: () => api.get('/api/v1/supplements/user-supplements/?limit=100'),
  updateUserSupplement: (id, data) => api.put(`/api/v1/supplements/user-supplements/${id}`, data),
  deleteUserSupplement: (id) => api.delete(`/api/v1/supplements/user-supplements/${id}`),
  // Translation endpoints (on-demand, cached permanently)
  translateAnalysis: (analysisId, targetLanguage) => api.post(
    `/api/v1/supplements/analyze-supplement-photo/${analysisId}/translate`,
    null,
    { params: { target_language: targetLanguage } }
  ),
  // User supplement notes translation (on-demand, cached permanently)
  translateNotes: (userSupplementId, targetLanguage) => api.post(
    `/api/v1/supplements/user-supplements/${userSupplementId}/translate-notes`,
    null,
    { params: { target_language: targetLanguage } }
  ),
  // Supplement Knowledge (AI-generated insights)
  generateAllKnowledge: (forceRegenerate = false) => api.post(
    '/api/v1/supplements/knowledge/generate-all',
    null,
    { params: { force_regenerate: forceRegenerate } }
  ),
  getKnowledgeStatus: () => api.get('/api/v1/supplements/knowledge/status'),
  getKnowledge: (supplementId, targetLanguage = 'en') => api.get(
    `/api/v1/supplements/knowledge/${supplementId}`,
    { params: { target_language: targetLanguage } }
  ),
  translateKnowledge: (supplementId, targetLanguage) => api.post(
    `/api/v1/supplements/knowledge/${supplementId}/translate`,
    null,
    { params: { target_language: targetLanguage } }
  ),
  // VH-81: Purpose / benefits summary (click-to-generate, cached per language)
  generatePurposeSummary: (supplementId, language) => api.post(
    `/api/v1/supplements/${supplementId}/purpose-summary/generate`,
    null,
    { params: { language } }
  ),
}

// Equipment API
export const equipmentAPI = {
  getAll: (params) => api.get('/api/v1/equipment', { params }),
  // VH-142 — which exercises this machine is used by. Same rule as the
  // "In Exercise (N)" count, so the list always has exactly N entries.
  getUsedByExercises: (id) => api.get(`/api/v1/equipment/${id}/exercises`),
  create: (data) => api.post('/api/v1/equipment', data),
  // VH-130: `confirmDuplicateName` lets a move proceed when the target
  // location already has a machine with this name. The server refuses with 409
  // without it, so the confirmation cannot be skipped by another client.
  update: (id, data, confirmDuplicateName = false) =>
    api.put(
      `/api/v1/equipment/${id}${confirmDuplicateName ? '?confirm_duplicate_name=true' : ''}`,
      data
    ),
  delete: (id, force = false) => api.delete(`/api/v1/equipment/${id}?force=${force}`),
  getTypes: () => api.get('/api/v1/equipment/types/list'),
  getMuscleGroups: () => api.get('/api/v1/equipment/muscle-groups/list'),
  translate: (id, targetLanguage) => api.post(`/api/v1/equipment/${id}/translate?target_language=${targetLanguage}`),
  // VH-131: one photo per machine. The original file is sent as-is — the server
  // converts HEIC and caps the long edge, so quality is decided there rather
  // than by whatever the browser felt like re-encoding.
  uploadPhoto: (id, file) => {
    const form = new FormData()
    form.append('file', file)
    // No explicit Content-Type: the browser must set the multipart boundary,
    // and naming the header ourselves strips it.
    return api.post(`/api/v1/equipment/${id}/photo`, form)
  },
  deletePhoto: (id) => api.delete(`/api/v1/equipment/${id}/photo`),
  // VH-126 Stage D: draft an exercise for a machine nothing uses yet. Writes
  // nothing — the response is a draft the user edits and saves.
  // `file` is optional: omitted, the server falls back to the photo it already
  // holds for this machine. Appending an empty/absent file would send the
  // literal string "undefined" as the upload, which the server would then try
  // to decode as an image.
  // VH-134: make an exercise you already have available on this machine.
  // Links, never copies — one exercise row, several places to do it.
  assignExercise: (equipmentId, exerciseId) =>
    api.post(`/api/v1/equipment/${equipmentId}/exercises/${exerciseId}`),

  proposeExercise: (id, file) => {
    const form = new FormData()
    if (file) form.append('file', file)
    return api.post(`/api/v1/equipment/${id}/propose-exercise`, form)
  },
}

// Workout Planner API
export const workoutPlannerAPI = {
  // Exercise Definitions
  // params may include: search_term, exercise_type, muscle_group, equipment_id,
  // difficulty_level, limit, skip, sort_by, sort_desc. Passing exercise_type /
  // muscle_group scopes the substitution picker to matching movements (bug ③).
  getExercises: (params) => api.get('/api/v1/workout-planner/exercises', { params }),

  // VH-143 — the N:M links between an exercise and the machines it can be
  // performed on. Automation (link_primary_and_twins) proposes these by name;
  // these two let the user correct it, which it cannot infer: the Squat Rack at
  // Avaloq Manegg also serves as a dip station.
  getExerciseAvailability: (exerciseId) =>
    api.get(`/api/v1/workout-planner/exercises/${exerciseId}/availability`),
  setExerciseAvailability: (exerciseId, equipmentIds) =>
    api.put(`/api/v1/workout-planner/exercises/${exerciseId}/availability`, {
      equipment_ids: equipmentIds,
    }),
  createExercise: (data) => api.post('/api/v1/workout-planner/exercises', data),
  updateExercise: (id, data) => api.put(`/api/v1/workout-planner/exercises/${id}`, data),
  deleteExercise: ({ id, force = false }) => api.delete(`/api/v1/workout-planner/exercises/${id}?force=${force}`),
  translateExercise: (id, targetLanguage) => api.post(`/api/v1/workout-planner/exercises/${id}/translate?target_language=${targetLanguage}`),

  // Training Templates (Exercise Blocks)
  getTemplates: (params) => api.get('/api/v1/workout-planner/templates', { params }),
  getTemplate: (id) => api.get(`/api/v1/workout-planner/templates/${id}`),
  createTemplate: (data) => api.post('/api/v1/workout-planner/templates', data),
  updateTemplate: (id, data) => api.put(`/api/v1/workout-planner/templates/${id}`, data),
  // VH-134: which of a module's exercises its location cannot do. Computed on
  // demand and never stored — coverage changes whenever a machine is added,
  // moved or deleted, and a stale answer would promise a session that will not
  // run.
  templateLocationCheck: (id) =>
    api.get(`/api/v1/workout-planner/templates/${id}/location-check`),
  deleteTemplate: (id) => api.delete(`/api/v1/workout-planner/templates/${id}`),
  addExerciseToTemplate: (templateId, data) => api.post(`/api/v1/workout-planner/templates/${templateId}/exercises`, data),
  updateTemplateExercise: (templateId, exerciseId, data) => api.put(`/api/v1/workout-planner/templates/${templateId}/exercises/${exerciseId}`, data),
  removeExerciseFromTemplate: (templateId, exerciseId) => api.delete(`/api/v1/workout-planner/templates/${templateId}/exercises/${exerciseId}`),
  reorderTemplateExercises: (templateId, exerciseIds) => api.post(`/api/v1/workout-planner/templates/${templateId}/reorder-exercises`, exerciseIds),
  // Exercise block translation endpoints (on-demand, cached permanently)
  translateTemplate: (id, targetLanguage) => api.post(`/api/v1/workout-planner/templates/${id}/translate?target_language=${targetLanguage}`),

  // AI Workout Plan Generation
  generateWorkoutPlan: (data) => api.post('/api/v1/workout-planner/generate', data),
  // VH-226 — save a reviewed block as one weekly program per week
  acceptWorkoutPlan: (data) => api.post('/api/v1/workout-planner/accept', data),

  // Exercise Field Templates (define input fields per exercise type)
  getFieldTemplates: (includeInactive = false) => api.get('/api/v1/workout-planner/field-templates', { params: { include_inactive: includeInactive } }),
}

// Weekly Training Programs API (New snapshot-based architecture)
export const weeklyProgramsAPI = {
  // Programs CRUD
  getPrograms: (params) => api.get('/api/v1/training-programs', { params }),
  getProgram: (id) => api.get(`/api/v1/training-programs/${id}`),
  getProgramByWeek: (year, week) => api.get(`/api/v1/training-programs/week/${year}/${week}`),
  // VH-207: a module's per-location variants. The GET is a read-only viewer —
  // which variant a session loads is decided by the session's location (VH-212).
  getModuleVariants: (templateId) => api.get(`/api/v1/training-programs/modules/${templateId}/variants`),
  resetModuleVariant: (templateId, locationId) =>
    api.post(`/api/v1/training-programs/modules/${templateId}/variants/${locationId}/reset`),
  deleteModuleVariant: (templateId, locationId) =>
    api.delete(`/api/v1/training-programs/modules/${templateId}/variants/${locationId}`),
  createProgram: (data) => api.post('/api/v1/training-programs', data),
  updateProgram: (id, data) => api.put(`/api/v1/training-programs/${id}`, data),
  deleteProgram: (id, force = false) => api.delete(`/api/v1/training-programs/${id}?force=${force}`),
  submitProgram: (id) => api.post(`/api/v1/training-programs/${id}/submit`),
  copyProgram: (id, data) => api.post(`/api/v1/training-programs/${id}/copy`, data),
  refreshTargets: (id) => api.post(`/api/v1/training-programs/${id}/refresh-targets`),

  // Sessions
  addSession: (programId, data) => api.post(`/api/v1/training-programs/${programId}/sessions`, data),
  addExerciseFromLibrary: (programId, data) => api.post(`/api/v1/training-programs/${programId}/exercises/from-library`, data),
  updateSession: (programId, sessionId, data) => api.put(`/api/v1/training-programs/${programId}/sessions/${sessionId}`, data),
  deleteSession: (programId, sessionId) => api.delete(`/api/v1/training-programs/${programId}/sessions/${sessionId}`),
  // VH-118: manually toggle a session's completion via dedicated endpoints
  // (independent of the VH-114 auto-complete rollup). SUBMITTED programs → 409.
  markSessionComplete: (sessionId) => api.post(`/api/v1/training-programs/sessions/${sessionId}/complete`),
  markSessionIncomplete: (sessionId) => api.post(`/api/v1/training-programs/sessions/${sessionId}/uncomplete`),

  // Session Exercises
  addExercise: (sessionId, data) => api.post(`/api/v1/training-programs/sessions/${sessionId}/exercises`, data),
  updateExercise: (exerciseId, data) => api.put(`/api/v1/training-programs/session-exercises/${exerciseId}`, data),
  // VH-206: the review filter — captured entries whose machine nobody stated.
  // Assignment goes through updateExercise with { performed_equipment_id }.
  getUnattributedExercises: (limit = 100) =>
    api.get('/api/v1/training-programs/session-exercises/unattributed', { params: { limit } }),
  updateExerciseSets: (exerciseId, data) => api.put(`/api/v1/training-programs/session-exercises/${exerciseId}/sets`, data),
  substituteExercise: (exerciseId, data) => api.post(`/api/v1/training-programs/session-exercises/${exerciseId}/substitute`, data),

  // VH-156: adding an exercise while reporting goes through the AUTHENTICATED
  // client, not captureService's anonymous one. That separation is deliberate:
  // the capture client never attaches the user JWT,
  // because a printed QR token is a capability to report one session — it must
  // never be able to change the plan. Loading and saving values stay
  // token-only; this route requires a login.
  addCaptureSessionExercise: (token, exerciseId) =>
    api.post(`/api/v1/capture/session/${token}/exercises`, { exercise_id: exerciseId }),

  // VH-212: state where the athlete is training THIS session. Authenticated for
  // the same reason as addCaptureSessionExercise above — a printed QR token
  // reports values, it does not restate where the training happened, and that
  // claim changes what the app offers for the rest of the session.
  //
  // `locationId: null` is a value, not an omission: it CLEARS the override and
  // returns the session to deriving its place from the module. That is the way
  // back from a mistaken tap, so it must reach the server as an explicit null.
  // The response carries the RESOLVED location, so callers re-render from the
  // server's answer rather than their own optimistic guess.
  // VH-295: undo a mistaken "Mark as completed" on the report page. Logged-in
  // owner only, like the two routes above; a submitted week answers 409.
  reopenCaptureSession: (token) =>
    api.post(`/api/v1/capture/session/${token}/reopen`),

  setCaptureSessionLocation: (token, locationId) =>
    api.put(`/api/v1/capture/session/${token}/location`, {
      location_id: locationId ?? null,
    }),
  skipExercise: (exerciseId, data) => api.post(`/api/v1/training-programs/session-exercises/${exerciseId}/skip`, data),
  unskipExercise: (exerciseId) => api.post(`/api/v1/training-programs/session-exercises/${exerciseId}/unskip`),
  getPersonalBest: (exerciseId, exerciseName) => api.get(`/api/v1/training-programs/exercises/${exerciseId}/personal-best`, {
    params: { exercise_name: exerciseName }
  }),

  // Reports
  getReport: (programId, userWeightKg = 80) => api.get(`/api/v1/training-programs/${programId}/report`, {
    params: { user_weight_kg: userWeightKg }
  }),

  // CR-18: Workout Photo Analysis
  // analyzer: 'gpt' (default) or 'google' (Google Cloud Vision)
  analyzeWorkoutPhoto: (sessionId, formData, analyzer = 'gpt', signal) =>
    api.post(`/api/v1/training-programs/sessions/${sessionId}/analyze-workout-photo?analyzer=${analyzer}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      ...(signal && { signal }),
    }),
  applyPhotoResults: (sessionId, data) =>
    api.post(`/api/v1/training-programs/sessions/${sessionId}/apply-photo-results`, data),

  // CR-15: Workout Scoring
  getScore: (programId) => api.get(`/api/v1/training-programs/${programId}/score`),
  getScoreBreakdown: (programId) => api.get(`/api/v1/training-programs/${programId}/score/breakdown`),
  getScoreTrends: (weeks = 12) => api.get('/api/v1/training-programs/scores/trends', { params: { weeks } }),
  getScoreRankings: () => api.get('/api/v1/training-programs/scores/rankings'),
  getSessionScore: (sessionId) => api.get(`/api/v1/training-programs/sessions/${sessionId}/score`),
  // VH-64: Per-exercise progress
  getExerciseSummary: () => api.get('/api/v1/training-programs/exercise-summary'),
  getExerciseHistory: (exerciseName, params) => api.get(`/api/v1/training-programs/exercise-history/${encodeURIComponent(exerciseName)}`, { params }),
}

// Nutrition API
export const nutritionAPI = {
  getFoodLogs: (params) => api.get('/api/v1/nutrition/food-logs', { params }),
  createFood: (data) => api.post('/api/v1/nutrition/foods', data),
  getMealsByDate: (date) => api.get(`/api/v1/nutrition/meals/by-date/${date}`),
  updateMeal: (id, data) => api.put(`/api/v1/nutrition/meals/${id}`, data),
  deleteMeal: (id) => api.delete(`/api/v1/nutrition/meals/${id}`),

  // VH-141 — "same as last time". Copy every meal of one type from the most
  // recent earlier day that has it onto the target day.
  getMealCopySource: (mealType, logDate, lookbackDays = 14) =>
    api.get('/api/v1/nutrition/meals/copy-source', {
      params: { meal_type: mealType, log_date: logDate, lookback_days: lookbackDays },
    }),
  // VH-181: copy ONE meal. `copyMeals` below takes a whole meal TYPE from a
  // day, which is not what "copy this meal" means to a user with five separate
  // lunch rows.
  // VH-248: what was eaten lately, each different meal once — the phone page's "Recent".
  getRecentMeals: (logDate, lookbackDays = 14) =>
    api.get('/api/v1/nutrition/meals/recent', { params: { log_date: logDate, lookback_days: lookbackDays } }),
  copyMeal: (mealId, logDate) =>
    api.post(`/api/v1/nutrition/meals/${mealId}/copy?log_date=${logDate}`),

  copyMeals: (mealType, sourceDate, logDate) =>
    api.post('/api/v1/nutrition/meals/copy', null, {
      params: { meal_type: mealType, source_date: sourceDate, log_date: logDate },
    }),
  // Meal food CRUD
  updateMealFood: (id, data) => api.put(`/api/v1/nutrition/meal-foods/${id}`, data),
  deleteMealFood: (id) => api.delete(`/api/v1/nutrition/meal-foods/${id}`),
  // Add AI-analyzed food to meal (creates food entry if needed)
  addAnalyzedFoodToMeal: (mealId, { food_name, quantity, quantity_unit, calories, protein, carbs, fat }) =>
    api.post(`/api/v1/nutrition/meals/${mealId}/foods/analyzed?food_name=${encodeURIComponent(food_name)}&quantity=${quantity}&quantity_unit=${encodeURIComponent(quantity_unit)}&calories=${calories}&protein=${protein}&carbs=${carbs}&fat=${fat}`),
  analyzeFoodPhoto: (formData, userHint = null) => {
    const params = userHint ? `?user_hint=${encodeURIComponent(userHint)}` : ''
    return api.post(`/api/v1/nutrition/analyze-food-photo${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  saveFoodPhotoAnalysis: (data) => api.post('/api/v1/nutrition/save-food-photo-analysis', data),
  getCurrentWeekReport: () => api.get('/api/v1/nutrition/reports/weekly/current'),
  // Manual text-based food entry
  analyzeFoodText: (data) => api.post('/api/v1/nutrition/analyze-text', data),
  saveTextFoodEntry: (data) => api.post('/api/v1/nutrition/save-text-entry', data),
  // Simple single food item analysis (for inline add food)
  analyzeFoodItem: (description) => api.post(`/api/v1/nutrition/analyze-food-item?description=${encodeURIComponent(description)}`),
  // Food name translations (on-demand, cached permanently)
  translateFoodNamesBatch: (foodIds, targetLanguage) => {
    const params = foodIds.map(id => `food_ids=${id}`).join('&')
    return api.post(`/api/v1/nutrition/foods/translate-batch?${params}&target_language=${targetLanguage}`)
  },
  // Favorite meals
  getFavoriteMeals: (params) => api.get('/api/v1/nutrition/favorite-meals', { params }),
  saveAsFavorite: (mealId, name, description) => {
    const params = new URLSearchParams({ name })
    if (description) params.append('description', description)
    return api.post(`/api/v1/nutrition/meals/${mealId}/save-as-favorite?${params}`)
  },
  addFavoriteToToday: (favoriteId, mealType, logDate) => {
    const params = new URLSearchParams()
    if (mealType) params.append('meal_type', mealType)
    if (logDate) params.append('log_date', logDate)
    const queryString = params.toString()
    return api.post(`/api/v1/nutrition/favorite-meals/${favoriteId}/add-to-today${queryString ? '?' + queryString : ''}`)
  },
  updateFavoriteMeal: (id, data) => api.put(`/api/v1/nutrition/favorite-meals/${id}`, data),
  deleteFavoriteMeal: (id) => api.delete(`/api/v1/nutrition/favorite-meals/${id}`),
  getFavoriteCategories: () => api.get('/api/v1/nutrition/favorite-meals/categories'),
  reorderFavoriteMeals: (orderedIds) => api.put('/api/v1/nutrition/favorite-meals/reorder', { ordered_ids: orderedIds }),
  translateFavoriteMealsBatch: (favoriteIds, targetLanguage) => {
    const params = favoriteIds.map(id => `favorite_ids=${id}`).join('&')
    return api.post(`/api/v1/nutrition/favorite-meals/translate-batch?${params}&target_language=${targetLanguage}`)
  },
  // VH-249: moved here when the meal-prep API went; the endpoint was always nutrition's.
  searchFoods: (q) => api.get('/api/v1/nutrition/foods/search', { params: { q, limit: 10 } }),
  // Email intake
  getEmailIntakeSettings: () => api.get('/api/v1/nutrition/email-intake/settings'),
  updateEmailIntakeAllowedSenders: (allowedSenders) => api.put('/api/v1/nutrition/email-intake/allowed-senders', { allowed_senders: allowedSenders }),
  // VH-49: Multi-week nutrition history
  getWeeklyHistory: (weeks = 4) => api.get('/api/v1/nutrition/reports/weekly-history', { params: { weeks } }),
}

// Daily Activity API (VH-47)
export const dailyActivityAPI = {
  getLatest: () => api.get('/api/v1/daily-activity/latest'),
  // VH-51: Multi-week summary with daily logs
  getMultiWeekSummary: (params) => api.get('/api/v1/daily-activity/multi-week-summary', { params }),
  // VH-54: Data Correction — CRUD
  createLog: (data) => api.post('/api/v1/daily-activity/logs', data),
  updateLog: (logId, data) => api.put(`/api/v1/daily-activity/${logId}`, data),
  deleteLog: (logId) => api.delete(`/api/v1/daily-activity/${logId}`),
  // VH-65: Upload Garmin sleep screenshot
  uploadScreenshot: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/api/v1/daily-activity/upload-screenshot', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
}

// Calorie Adherence API (VH-45)
export const calorieAdherenceAPI = {
  getAdherence: (params) => api.get('/api/v1/progress/calorie-adherence', { params }),
}

// Sleep & Recovery API (VH-224)
export const sleepRecoveryAPI = {
  getTrends: (range) => api.get('/api/v1/progress/sleep-recovery', { params: { range } }),
  startExperiment: (payload) => api.post('/api/v1/progress/sleep-experiments', payload),
  endExperiment: (id) => api.post(`/api/v1/progress/sleep-experiments/${id}/end`),
}

// Training Analysis API (VH-225)
export const trainingAnalysisAPI = {
  getAnalysis: (range) => api.get('/api/v1/training-analysis', { params: { range } }),
  getVariantSuggestions: () => api.get('/api/v1/training-analysis/variant-suggestions'),
  createVariantLink: (payload) => api.post('/api/v1/exercise-variants', payload),
  rejectVariantLink: (id) => api.post(`/api/v1/exercise-variants/${id}/reject`),
}

// Coupling analysis API (VH-222)
export const couplingAnalysisAPI = {
  start: () => api.post('/api/v1/analysis/couplings'),
  getLatest: () => api.get('/api/v1/analysis/couplings/latest'),
  getRun: (runId) => api.get(`/api/v1/analysis/runs/${runId}`),
}

// Meal prep API (VH-223) — every plan call returns the complete recomputed plan

// VH-229 refinement: the weekly plan document of a cycle week, and one-click logging of plan meals
export const planDocumentAPI = {
  get: (cycleId, planId, language = null) =>
    api.get(`/api/v1/health-cycles/${cycleId}/weeks/${planId}/plan-document`, language ? { params: { language } } : undefined),
  setMappings: (cycleId, planId, mappings) =>
    api.put(`/api/v1/health-cycles/${cycleId}/weeks/${planId}/plan-document/food-mappings`, { mappings }),
  // Bulk mapping: every plan ingredient with its mapping and a best-guess food; food search with German synonyms
  mappingSuggestions: (cycleId, planId, language = null) =>
    api.get(`/api/v1/health-cycles/${cycleId}/weeks/${planId}/plan-document/mapping-suggestions`, language ? { params: { language } } : undefined),
  searchMappingFoods: (cycleId, planId, q, language = null) =>
    api.get(`/api/v1/health-cycles/${cycleId}/weeks/${planId}/plan-document/food-search`, { params: language ? { q, language } : { q } }),
  // VH-236: what may be added to a plan meal — foods and the user's own recipes
  searchPlanItems: (cycleId, planId, q, language = null) =>
    api.get(`/api/v1/health-cycles/${cycleId}/weeks/${planId}/plan-document/item-search`, { params: language ? { q, language } : { q } }),
  listForLogging: (language = null) => api.get('/api/v1/nutrition/plan-meals', language ? { params: { language } } : undefined),
  logMeals: (payload) => api.post('/api/v1/nutrition/plan-meals/log', payload),
  // Planning a week: same as last week, or a new plan under review (preview) that is edited, accepted or discarded
  copyPrevious: (cycleId, weekStart, replace = false) =>
    api.post(`/api/v1/health-cycles/${cycleId}/weeks/plan-document/copy-previous`, { week_start: weekStart, replace }),
  startPreview: (cycleId, planId, language, instruction = null) =>
    api.post(`/api/v1/health-cycles/${cycleId}/weeks/${planId}/plan-document/preview`, { language, instruction }),
  getPreview: (cycleId, planId) => api.get(`/api/v1/health-cycles/${cycleId}/weeks/${planId}/plan-document/preview`),
  adjust: (cycleId, planId) => api.post(`/api/v1/health-cycles/${cycleId}/weeks/${planId}/plan-document/preview/adjust`),
  editPreview: (cycleId, planId, edits) =>
    api.patch(`/api/v1/health-cycles/${cycleId}/weeks/${planId}/plan-document/preview`, { edits }),
  acceptPreview: (cycleId, planId) => api.post(`/api/v1/health-cycles/${cycleId}/weeks/${planId}/plan-document/preview/accept`),
  discardPreview: (cycleId, planId) => api.delete(`/api/v1/health-cycles/${cycleId}/weeks/${planId}/plan-document/preview`),
}

// Health cycle API (VH-227) — one cycle from blood test to blood test
export const healthCyclesAPI = {
  list: () => api.get('/api/v1/health-cycles'),
  get: (cycleId) => api.get(`/api/v1/health-cycles/${cycleId}`),
  start: ({ bloodTestId, questions = [], discardExistingDraft = false }) =>
    api.post('/api/v1/health-cycles', {
      blood_test_id: bloodTestId,
      questions,
      discard_existing_draft: discardExistingDraft,
    }),
  getVersion: (cycleId, number) => api.get(`/api/v1/health-cycles/${cycleId}/versions/${number}`),
  compare: (cycleId, a, b) => api.get(`/api/v1/health-cycles/${cycleId}/compare`, { params: { a, b } }),
  history: (cycleId) => api.get(`/api/v1/health-cycles/${cycleId}/history`),
  retryStep: (cycleId, step) => api.post(`/api/v1/health-cycles/${cycleId}/steps/${step}/retry`),
  rerun: (cycleId) => api.post(`/api/v1/health-cycles/${cycleId}/rerun`),
  resume: (cycleId) => api.post(`/api/v1/health-cycles/${cycleId}/resume`),
  finalizePreview: (cycleId) => api.get(`/api/v1/health-cycles/${cycleId}/finalize`),
  finalize: (cycleId) => api.post(`/api/v1/health-cycles/${cycleId}/finalize`),
  // VH-231: goal progress until the next blood test
  goalProgress: () => api.get('/api/v1/health-cycles/current/goal-progress'),
  checkAction: (cycleId, recommendationId, checked) =>
    api.put(`/api/v1/health-cycles/${cycleId}/actions/${recommendationId}/check`, { checked }),
  listMessages: (cycleId) => api.get(`/api/v1/health-cycles/${cycleId}/messages`),
  sendMessage: (cycleId, content, language) =>
    api.post(`/api/v1/health-cycles/${cycleId}/messages`, { content, language }),
  // Deletes the chat only; change sets stay (open ones remain confirmable)
  clearMessages: (cycleId) => api.delete(`/api/v1/health-cycles/${cycleId}/messages`),
  // accepted_operation_ids: null = all operations; the server checks the base version (409 when stale)
  confirmChangeSet: (cycleId, changeSetId, acceptedOperationIds = null) =>
    api.post(`/api/v1/health-cycles/${cycleId}/change-sets/${changeSetId}/confirm`, {
      accepted_operation_ids: acceptedOperationIds,
    }),
  rejectChangeSet: (cycleId, changeSetId) =>
    api.post(`/api/v1/health-cycles/${cycleId}/change-sets/${changeSetId}/reject`),
  remove: (cycleId) => api.delete(`/api/v1/health-cycles/${cycleId}`, { params: { confirm: true } }),
  // VH-228 health report and recommendation decisions
  getHealthReport: (cycleId, number) => api.get(`/api/v1/health-cycles/${cycleId}/versions/${number}/health-report`),
  decideRecommendation: (cycleId, recId, isAccepted, userNotes = null) =>
    api.put(`/api/v1/health-cycles/${cycleId}/recommendations/${recId}`, { is_accepted: isAccepted, user_notes: userNotes }),
  // VH-230 follow-up against the finalized baseline
  getFollowUp: (cycleId, version = null) =>
    api.get(`/api/v1/health-cycles/${cycleId}/followup`, version ? { params: { version } } : undefined),
  chooseFollowUpBaseline: (cycleId, versionId) =>
    api.post(`/api/v1/health-cycles/${cycleId}/followup/baseline`, { version_id: versionId }),
  // VH-229 nutrition report and weeks
  getNutritionReport: (cycleId, number) => api.get(`/api/v1/health-cycles/${cycleId}/versions/${number}/nutrition-report`),
  listWeeks: (cycleId) => api.get(`/api/v1/health-cycles/${cycleId}/weeks`),
  addWeek: (cycleId, payload = {}) => api.post(`/api/v1/health-cycles/${cycleId}/weeks`, payload),
  deleteWeek: (cycleId, planId) => api.delete(`/api/v1/health-cycles/${cycleId}/weeks/${planId}`),
  setRestDay: (cycleId, planId, weekday) => api.put(`/api/v1/health-cycles/${cycleId}/weeks/${planId}/rest-day`, { weekday }),
  // VH-228 phase 0: stored, printable cycle documents (health report, weekly plans)
  listDocuments: (cycleId) => api.get(`/api/v1/health-cycles/${cycleId}/documents`),
  // VH-233: supplementary tests (attach/detach rerun the cycle) and what a revision changed
  listSupplementaryTests: (cycleId) => api.get(`/api/v1/health-cycles/${cycleId}/supplementary-tests`),
  attachSupplementaryTest: (cycleId, bloodTestId) =>
    api.post(`/api/v1/health-cycles/${cycleId}/supplementary-tests`, { blood_test_id: bloodTestId }),
  detachSupplementaryTest: (cycleId, bloodTestId) =>
    api.delete(`/api/v1/health-cycles/${cycleId}/supplementary-tests/${bloodTestId}`),
  getChangeSummary: (cycleId, number) => api.get(`/api/v1/health-cycles/${cycleId}/versions/${number}/change-summary`),
  // VH-234: tests to request, ordered ticks and the planned draw date
  getTestChecklist: (cycleId, language) =>
    api.get(`/api/v1/health-cycles/${cycleId}/test-checklist`, { params: { language } }),
  setTestOrdered: (cycleId, code, ordered, language) =>
    api.put(`/api/v1/health-cycles/${cycleId}/test-checklist/${encodeURIComponent(code)}`, { ordered }, { params: { language } }),
  setPlannedDraw: (cycleId, plannedDrawAt, language) =>
    api.put(`/api/v1/health-cycles/${cycleId}/planned-draw`, { planned_draw_at: plannedDrawAt }, { params: { language } }),
  getDocument: (cycleId, documentId, language) =>
    api.get(`/api/v1/health-cycles/${cycleId}/documents/${documentId}`, language ? { params: { language } } : undefined),
}

// Dashboard API
// VH-235: what the user has told the app.
export const memoryAPI = {
  list: (params) => api.get('/api/v1/memories', { params }),
  create: (data) => api.post('/api/v1/memories', data),
  update: (id, data) => api.put(`/api/v1/memories/${id}`, data),
  confirm: (id) => api.post(`/api/v1/memories/${id}/confirm`),
  remove: (id) => api.delete(`/api/v1/memories/${id}`),
  exportFile: () => api.get('/api/v1/memories/export', { responseType: 'blob' }),
}

export const dashboardAPI = {
  getSummary: () => api.get('/api/v1/dashboard/summary'),
}

// Admin API
export const adminAPI = {

  // VH-19: User Management (RBAC)
  listUsers: (params) => api.get('/api/v1/admin/users', { params }),
  changeUserRole: (userId, role) => api.put(`/api/v1/admin/users/${userId}/role`, { role }),
  // VH-287: add a user (a set-up mail goes out) and send that mail again.
  addUser: (user) => api.post('/api/v1/admin/users', user),
  sendSetupMail: (userId) => api.post(`/api/v1/admin/users/${userId}/setup-mail`),
  // VH-317: any admin may unlink a user's HubPlatform account.
  unlinkHubPlatform: (userId) => api.delete(`/api/v1/admin/users/${userId}/hubplatform-link`),

  // VH-71 / VH-117: LLM Settings (per-analyzer provider + model).
  // getPhotoAnalysisSettings -> { analyzers: [{ analyzer_name, provider,
  //   model_name, available_models, provider_locked, api_key_configured }] }
  // updatePhotoAnalysisSettings payload -> { analyzers: [{ analyzer_name,
  //   provider, model }] } (model is REQUIRED per analyzer).
  // VH-258: every AI feature's model, with the provider's own list behind it.
  getAIModelSettings: (refresh = false) =>
    api.get('/api/v1/admin/settings/ai-models', { params: refresh ? { refresh: true } : {} }),
  updateAIModelSettings: (features) => api.put('/api/v1/admin/settings/ai-models', { features }),
  // VH-280: OpenAI calls per feature, this week against last (from the ai_calls log).
  getAIUsage: () => api.get('/api/v1/admin/settings/ai-models/usage'),
  // VH-280: comparison runs (started from the command line on vh-sit) and the blind review.
  listModelTrials: () => api.get('/api/v1/admin/model-trials'),
  getModelTrialReview: (runId) => api.get(`/api/v1/admin/model-trials/${runId}/review`),
  submitModelTrialReview: (runId, answerId, pick) =>
    api.post(`/api/v1/admin/model-trials/${runId}/review`, { answer_id: answerId, pick }),
  getPhotoAnalysisSettings: () => api.get('/api/v1/admin/settings/photo-analysis'),
  updatePhotoAnalysisSettings: (settings) => api.put('/api/v1/admin/settings/photo-analysis', settings),

  // VH-19: Coach Assignments
  listCoachAssignments: (params) => api.get('/api/v1/admin/coach-assignments', { params }),
  createCoachAssignment: (coachId, userId) => api.post('/api/v1/admin/coach-assignments', { coach_id: coachId, user_id: userId }),
  deleteCoachAssignment: (assignmentId) => api.delete(`/api/v1/admin/coach-assignments/${assignmentId}`),
}

// Health Reports API
export const reportsAPI = {
  getAll: (params) => api.get('/api/v1/health-reports/', { params }),
  getById: (id) => api.get(`/api/v1/health-reports/${id}`),
  // VH-55: generate accepts optional { start_date, end_date } (ISO date strings)
  generate: (payload) => api.post('/api/v1/health-reports/comprehensive', payload || {}),
  // VH-67: generateWeekly accepts optional { start_week, start_year, end_week, end_year }
  // for multi-week range reports. No payload = today's behavior (previous ISO week).
  generateWeekly: (payload) => api.post('/api/v1/health-reports/weekly', payload || {}),
  // VH-67: translate AI narrative on a Health Report row
  translateReport: (reportId, targetLanguage) => api.post(
    `/api/v1/health-reports/${reportId}/translate`,
    null,
    { params: { target_language: targetLanguage } }
  ),

  // VH-55: Conversational follow-up dialog
  listChat: (reportId) => api.get(`/api/v1/health-reports/${reportId}/chat`),
  sendChat: (reportId, content) =>
    api.post(`/api/v1/health-reports/${reportId}/chat`, { content }),

  // VH-55: Recommendation tracking
  acceptRecommendation: (recommendationId) =>
    api.put(`/api/v1/health-reports/recommendations/${recommendationId}/accept`),
  rejectRecommendation: (recommendationId) =>
    api.put(`/api/v1/health-reports/recommendations/${recommendationId}/reject`),
  completeRecommendation: (recommendationId) =>
    api.put(`/api/v1/health-reports/recommendations/${recommendationId}/complete`),
}

// Profile API (Goals, Measurements, Health Metrics)
export const profileAPI = {
  // Health Metrics
  getHealthMetrics: (activityLevel) => {
    const params = activityLevel ? { activity_level: activityLevel } : {}
    return api.get('/api/v1/profile/health-metrics', { params })
  },
  calculateGoals: (data) => api.post('/api/v1/profile/calculate-goals', data),

  // Goals
  getCurrentGoal: (date) => (date
    ? api.get('/api/v1/profile/goals/current', { params: { date } })
    : api.get('/api/v1/profile/goals/current')),
  createGoal: (data) => api.post('/api/v1/profile/goals', data),
  updateGoal: (id, data) => api.put(`/api/v1/profile/goals/${id}`, data),

  // Body Measurements
  getLatestMeasurement: () => api.get('/api/v1/profile/measurements/latest'),
  getMeasurementHistory: (params) => api.get('/api/v1/profile/measurements/history', { params }),
  createMeasurement: (data) => api.post('/api/v1/profile/measurements', data),

  // Password Change
  changePassword: (data) => api.post('/api/v1/profile/change-password', data),

  // AI Body Analysis
  getAIBodyAnalysis: (data) => api.post('/api/v1/profile/ai-analysis', data),

  // Scale Screenshot Analysis
  analyzeScaleScreenshot: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/api/v1/profile/analyze-scale-screenshot', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
}